import { useMemo } from 'react'
import { useAuth } from '@/shared/context/AuthContext'
import { UserTypes } from '@/shared/types/auth'

interface UserTypeManager {
    hasType: (type: UserTypes) => boolean
    hasAnyType: (types: UserTypes[]) => boolean
    hasAllTypes: (types: UserTypes[]) => boolean
    getTypes: () => UserTypes[]
}

/**
 * Custom Hook to check permissions
 *
 * @returns PermissionManager
 *
 * Example of using the usePermissions hook:
 ```jsx
  function ParentActions() {
    const userTypes = useUserTypes();
    
    const isParent = userTypes.hasType(UserTypes.IS_PARENT);
    const isTeacherOrAdmin = userTypes.hasAnyType([
      UserTypes.IS_TEACHER, 
      UserTypes.IS_ADMIN_TEACHER
    ]);

    return (
      <div>
        {isTeacherOrAdmin && <TeacherControls />}
        {isParent && <ParentControls />}
      </div>
    );
  }
 ```
 */

export function useUserTypes(): UserTypeManager {
    const { userTypes } = useAuth()

    return useMemo(
        () => ({
            hasType: (type: UserTypes): boolean => userTypes?.includes(type) || false,

            hasAnyType: (types: UserTypes[]): boolean =>
                userTypes?.some((type) => types.includes(type)) || false,

            hasAllTypes: (types: UserTypes[]): boolean =>
                types.every((type) => userTypes?.includes(type)) || false,

            getTypes: () => userTypes || []
        }),
        [userTypes]
    )
}
